import * as Actions from '../actions/importExportActions';
import ImportExport from '../../entities/importExport';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type ImportExportAction = { 
    type: string; 
    importExport: ImportExport | null; 
    importExportId: string | ImportExport; 
    partial: boolean; 
    lastFetch: number; 
    importExports: ImportExport[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<ImportExport> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {ImportExport[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<ImportExport> | null, firmId: string, userId: string, updates?: ImportExport[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'importExport', 'importExports', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<ImportExport>} data
 * @param {ImportExport[]} [updates]
 * @returns {ArrayMap<ImportExport>}
 */
function updateStorage(data: ArrayMap<ImportExport>, updates?: ImportExport[]): ArrayMap<ImportExport> {
	if (logging) console.debug('⮂ importExportReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {ImportExport[]} [updates]
 * @returns {ArrayMap<ImportExport>}
 */
function updateStorageNoReturn(updates?: ImportExport[]): void {
	if (logging) console.debug('⮂ importExportReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {ImportExport[]} [data]
 * @returns {ArrayMap<ImportExport>}
 */
function createState(data?: ImportExport[] | ArrayMap<ImportExport>): ArrayMap<ImportExport> {
	if (!data) return ArrayMapRegistry.clone<ImportExport>('importExport', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<ImportExport>('importExport', data.map(e => e));
    else return ArrayMapRegistry.clone<ImportExport>('importExport', data);
}

function importExports(state: ArrayMap<ImportExport>, action: ImportExportAction): ArrayMap<ImportExport> {
	if (!state) state = createState();

	let st: ArrayMap<ImportExport>;

	switch (action.type) {
		// action.importExport - the new value of importExport with id action.id
		// action.importExportId - id of the target, used if new value is null
		case Actions.IMPORTEXPORT_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ importExportReducer: FETCH_ONE_SUCCESS', action);

			if (action.importExport === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.importExportId)) {
				st = state.clone();
				st.set(action.importExport);
				// state.clear();

				updateStorageNoReturn([action.importExport]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.importExports
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.importExports
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.importExports
			 !partial, !stateEmpty, fetchEmpty	-> D return importExports in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge importExports in state newer than those in action.importExports
		 */
		case Actions.IMPORTEXPORT_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.importExports.length === 0;
			const fetched = action.importExports.map(importExport => new ImportExport({...importExport, persisted: true}));

			if (logging) console.debug(`⮂ importExportReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} importExports with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ importExportReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ importExportReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<ImportExport> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: ImportExport[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(importExport => {
					const current = st.get(importExport.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && importExport.status === 'D') {
							// Remove it from state
							toUpdate.push(importExport);
							st.delete(importExport);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (importExport.addedOn > current.addedOn) {
								toUpdate.push(importExport);
								st.set(importExport);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (importExport.status !== 'D') {
							toUpdate.push(importExport);
							st.set(importExport);
						}
						else if (logging) console.debug('⮂ importExportReducer: partialMerge: skipping deleted importExport not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ importExportReducer: empty: importExports', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ importExportReducer: fullMerge: importExports', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localImportExport => {
                    // If this importExport has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localImportExport.persisted) {
                        const fetchedImportExport = st.get(localImportExport.id);
                        if (!fetchedImportExport || localImportExport.addedOn > fetchedImportExport.addedOn) st.set(localImportExport);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.IMPORTEXPORT_FETCH_ONE_FAILURE:
			console.error('IMPORTEXPORT_FETCH_ONE_FAILURE', action.importExportId, action.err);

			// No update to storage required
			return state;

		case Actions.IMPORTEXPORT_FETCH_FAILURE:
			console.error('IMPORTEXPORT_FETCH_FAILURE', action.importExportId, action.err);

			// No update to storage required
			return state;

		case Actions.IMPORTEXPORT_RESTORE: {
		    let importExport = new ImportExport({...action.importExport});
            st = state.clone();

            const existing = st.get(importExport.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (importExport.addedOn >= existing.addedOn) {
                    st.set(importExport);
                    return updateStorage(st, [importExport]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (importExport.status === 'D') return state;

                st.set(importExport);
                return updateStorage(st, [importExport]);
            }
        }
            
		case Actions.IMPORTEXPORT_SAVE: {
		    // console.debug('⮂ IMPORTEXPORT_SAVE reducer', action.importExport?.id);
		    
			// clone the importExport
			let importExport = new ImportExport({...action.importExport});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new importExport in the state
			// If this is a create, add to the queue
			const _importExport = st.get(importExport.id);

			if (!_importExport) st.set(importExport);
			else {
				if (importExport.addedOn >= _importExport.addedOn) st.set(importExport);
			}

			return updateStorage(st, [importExport]);
		}

		case Actions.IMPORTEXPORT_SAVE_MANY: {
		    if (logging) console.debug('⮂ IMPORTEXPORT_SAVE_MANY reducer', action.importExports.map(importExport => importExport.id).join(','));
		    
			// clone the importExports
			let toProcess = action.importExports.map(importExport => new ImportExport(importExport));

			st = state.clone();
			// state.clear();

			const updates: ImportExport[] = [];

			toProcess.forEach(importExport => {
				// Add or update the new importExport in the state
				// If this is a create, add to the queue
				const existing = st.get(importExport.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (importExport.status !== 'D') {
						st.push(importExport);
						updates.push(importExport);
					}
					else if (logging) console.debug('⮂ IMPORTEXPORT_SAVE_MANY: skipping deleted importExport not in state');
				}
				else {
					if (importExport.addedOn >= existing.addedOn) {
						st.set(importExport);
						updates.push(importExport);
					}
					else if (logging) console.debug('⮂ Skipping, existing importExport had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.IMPORTEXPORT_REMOVE: {
			// clone the importExport
			const importExport = new ImportExport({...action.importExport});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(importExport);

			return updateStorage(st, [importExport]);
		}

		case Actions.IMPORTEXPORT_RESTORE_FAILURE:
		case Actions.IMPORTEXPORT_SAVE_FAILURE: {
		    console.warn('IMPORTEXPORT_SAVE_FAILURE reducer', action.importExport?.id);
		    if (!action.importExport?.id) {
				console.warn('IMPORTEXPORT_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const importExport = ensure(ImportExport, action.importExport);
			if (!importExport) {
				console.error('Could not find importExport after call to save');
				return state;
			}

			st = state.clone();

			const _importExportRef = state.get(importExport.id);

			if (_importExportRef && (_importExportRef.addedOn === importExport.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _importExport = new ImportExport({..._importExportRef});
				_importExport.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_importExport.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('importExport', _importExport).then(r => {
						if (r.isErr()) console.error(`⮂ importExportReducer: failed to save importExport with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ importExportReducer: saved importExport with fatalError info');
					}).catch(err => console.error(`⮂ importExportReducer: failed to save importExport with fatalError info: ${err.message}`));

				st.set(_importExport);
			}

			return updateStorage(st);
		}

		case Actions.IMPORTEXPORT_SAVE_MANY_FAILURE: {
		    console.warn('IMPORTEXPORT_SAVE_MANY_FAILURE reducer', action.importExports.map(importExport => importExport.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.IMPORTEXPORT_RESTORE_SUCCESS:
		case Actions.IMPORTEXPORT_SAVE_SUCCESS: {
			if (!action.importExport?.id) {
				console.warn('IMPORTEXPORT_SAVE_SUCCESS reducer');
                return state;
			}

			const importExportRef = state.get(action.importExport?.id);
			if (!importExportRef) {
				console.warn('Could not find importExport after call to save');
                return state;
			}

			st = state.clone();

			if (action.importExport.addedOn >= importExportRef.addedOn) {
				st.set(action.importExport);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const importExport = new ImportExport({...importExportRef, persisted: true});
				st.set(importExport);
			}

			return updateStorage(st, [action.importExport]);
		}

		case Actions.IMPORTEXPORT_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ IMPORTEXPORT_SAVE_MANY_SUCCESS reducer', action.importExports.map(importExport => importExport.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const importExports: ImportExport[] = [];

			action.importExports.forEach(e => {
				const importExportRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!importExportRef) console.warn('Could not find importExport after call to save');
				else if (e.addedOn >= importExportRef.addedOn) {
				    st.set(e);
				    importExports.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const importExport = new ImportExport({...importExportRef, persisted: true});
					st.set(importExport);
					importExports.push(importExport);
				}
			});

			return updateStorage(st, importExports);
		}

		case Actions.IMPORTEXPORT_REMOVE_FAILURE: {
		    console.warn('IMPORTEXPORT_REMOVE_FAILURE action', action.importExport?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.IMPORTEXPORT_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.IMPORTEXPORT_RESTORE_MANY: {
			if (logging) console.debug('⮂ IMPORTEXPORT_RESTORE_MANY reducer', action.importExports.map(importExport => importExport.id).join(','));

			const toProcess = action.importExports.map(importExport => new ImportExport({...importExport}));
			st = state.clone();

			const updates: ImportExport[] = [];

			toProcess.forEach(importExport => {
				const existing = st.get(importExport.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (importExport.addedOn >= existing.addedOn) {
						st.set(importExport);
						updates.push(importExport);
					}
					else if (logging) console.debug('⮂ IMPORTEXPORT_RESTORE_MANY: skipping older version of importExport');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (importExport.status !== 'D') {
						st.set(importExport);
						updates.push(importExport);
					}
					else if (logging) console.debug('⮂ IMPORTEXPORT_RESTORE_MANY: skipping deleted importExport not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.IMPORTEXPORT_REMOVE_MANY: {
			if (logging) console.debug('⮂ IMPORTEXPORT_REMOVE_MANY reducer', action.importExports.map(importExport => importExport.id).join(','));

			const toProcess = action.importExports.map(importExport => new ImportExport({...importExport}));
			st = state.clone();

			toProcess.forEach(importExport => {
				st.delete(importExport);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default importExports;